import { useEffect, useRef } from 'react';
import { useBooleanVariation, useBucketeerClient } from '@bucketeer/react-client-sdk';
import { BUCKETEER_FEATURE_IDS } from '@/utils/bucketeer';
import mixpanel from '@/utils/mixpanel';

/**
 * Returns whether the joyride onboarding tour is enabled for the current user
 * based on the Bucketeer 'joyride-onboarding' feature flag (50/50 experiment).
 *
 * Tracks variant assignment in Mixpanel once per session and sets a persistent
 * user property so we can tell if a user ever saw the joyride.
 *
 * @returns {{ joyrideEnabled: boolean, isReady: boolean }}
 */
const useJoyrideExperiment = () => {
  const client = useBucketeerClient();
  const joyrideEnabled = useBooleanVariation(BUCKETEER_FEATURE_IDS.JOYRIDE_ONBOARDING, false);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!client || hasTrackedRef.current) return;
    hasTrackedRef.current = true;

    const variant = joyrideEnabled ? 'show' : 'hide';

    mixpanel.track('Joyride Onboarding Variant Assigned', {
      variant,
      source: 'bucketeer',
    });

    mixpanel.people.set({
      'Joyride Onboarding Variant': variant,
    });

    if (joyrideEnabled) {
      mixpanel.people.set_once({
        'Has Seen Joyride': true,
      });
    }
  }, [client, joyrideEnabled]);

  return { joyrideEnabled, isReady: !!client };
};

export default useJoyrideExperiment;
